import { ActionModal, FormError, Label } from '@kcftech/design-system';
import {
    ICorporationResponse,
    ILocationResponse,
    IModelAssignmentResponse,
    IPropertyChild,
    ModelAssignmentUpdateRequest,
    PropertyValueType,
    SetPropertyChild,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { RadioButtonGroup } from 'components/forms/RadioButtonGroup';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { GroupHierarchyText } from 'components/group_hierarchy/GroupHierarchyText';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useAnalyticsModel } from 'custom_hooks/data_hooks/analytics_models/useAnalyticsModel';
import { useTreeItem } from 'custom_hooks/data_hooks/tree/useTreeItem';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import {
    booleanRadioOptions,
    getParameterInputRules,
    supportedPropertyValueTypes,
} from 'pages/Models/AssignedModalTab/shared';
import { ModelAggregateGroupsInput } from 'pages/workbench/side_panel/workbench_tree/modals/ModelAggregateGroupsInput';
import { useInitialTreeExpansion } from 'pages/workbench/side_panel/workbench_tree/modals/useInitialTreeExpansion';
import { useEffect, useMemo, useState } from 'react';
import {
    Controller,
    RegisterOptions,
    UseFormReturn,
    useFieldArray,
    useForm,
} from 'react-hook-form';
import styled from 'styled-components';

export type EditModelAssignmentModalProps = Readonly<{
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
    assignment: IModelAssignmentResponse;
    onClose: (result?: IModelAssignmentResponse) => void;
}>;

export type EditModelAssignmentFormData = {
    groupIds: string[];
    parameters: IPropertyChild[];
};

const groupRules: RegisterOptions = {
    validate: (groupIds: string[] | undefined) =>
        groupIds === undefined || groupIds.length === 0
            ? 'Please select a group.'
            : undefined,
};

const Columns = styled.div`
    @media ${MediaQueries.largeAndUp} {
        display: grid;
        height: 100%;
        grid-template-columns: 1fr 1fr;
        column-gap: 4rem;
    }
`;

const Column = styled.div`
    overflow-y: auto;
    padding: 2rem;
`;

const renderParameterInput = (
    form: UseFormReturn<EditModelAssignmentFormData>,
    property: IPropertyChild,
    index: number,
) => {
    if (!supportedPropertyValueTypes.includes(property.PropertyValueType)) {
        return <></>;
    }

    const {
        control,
        formState: { errors },
        register,
    } = form;

    const parameterPath = `parameters.${index}.PropertyValue` as const;
    const parameterErrors = errors.parameters?.[index];
    const rules = getParameterInputRules(property);

    switch (property.PropertyValueType) {
        case PropertyValueType.Double:
        case PropertyValueType.Float:
        case PropertyValueType.Integer:
        case PropertyValueType.String:
            return (
                <TextInput
                    placeholder={`Enter ${property.PropertyName}`}
                    className="my-10"
                    invalid={parameterErrors !== undefined}
                    {...register(parameterPath, rules)}
                />
            );
        case PropertyValueType.Boolean:
            return (
                <Controller
                    control={control}
                    name={parameterPath}
                    rules={rules}
                    render={({ field }) => (
                        <RadioButtonGroup
                            radioStyle="indicator"
                            layout="horizontal"
                            name={property.PropertyName}
                            items={booleanRadioOptions}
                            value={field.value}
                            onChange={field.onChange}
                            className="mt-20 mb-10"
                        />
                    )}
                />
            );
        default:
            return <></>;
    }
};

export function EditModelAssignmentModal({
    corporation,
    location,
    assignment,
    onClose,
}: EditModelAssignmentModalProps) {
    const { showToast } = useNotificationContext();

    const { data: model, isLoading: modelLoading } = useAnalyticsModel(
        assignment.Model.AnalyticsModelId,
    );

    const hierarchyPrefix = useMemo(() => {
        if (location && corporation) {
            return undefined;
        }
        if (corporation) {
            return assignment.Location.LocationName;
        }
        return `${assignment.Corporation.CorporationName} / ${assignment.Location.LocationName}`;
    }, [corporation, location, assignment]);

    const { data: rootGroup, isLoading: isLoadingRootGroup } = useTreeItem(
        assignment.RootGroup.RootGroupId,
    );
    const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
    const {
        data: initialExpandedIds,
        isLoading: isLoadingInitialExpansions,
    } = useInitialTreeExpansion(assignment.AssignedGroupIds);

    useEffect(() => {
        if (initialExpandedIds) {
            setExpandedGroupIds(initialExpandedIds);
        }
    }, [initialExpandedIds]);

    const form = useForm<EditModelAssignmentFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        defaultValues: {
            groupIds: assignment.AssignedGroupIds,
        },
    });

    const {
        formState: { isSubmitted, isSubmitting, isValid, errors },
        handleSubmit,
        setValue,
        control,
        register,
    } = form;

    const { fields: properties } = useFieldArray({
        name: 'parameters',
        control,
    });

    useEffect(() => {
        setValue(
            'parameters',
            model?.Properties.map((property) => ({
                ...(assignment.Properties.find(
                    (assignmentProp) => assignmentProp.PropertyId === property.PropertyId,
                ) ?? property),
            })) ?? [],
        );
    }, [assignment.Properties, model?.Properties, setValue]);

    const {
        mutate: saveAssignment,
        isPending: isSaving,
        isError: isSaveError,
    } = useMutation({
        meta: { name: 'update-analytics-assignment' },
        mutationFn: ({ groupIds, parameters }: EditModelAssignmentFormData) => {
            const request = new ModelAssignmentUpdateRequest({
                AssignedGroupIds: groupIds,
                Properties: parameters
                    .filter((param) => param.PropertyValue)
                    .map(
                        (param) =>
                            new SetPropertyChild({
                                PropertyId: param.PropertyId,
                                Value: param.PropertyValue ?? '',
                            }),
                    ),
            });

            return sdClient.updateAnalyticsAssignment({
                assignmentId: assignment.Id,
                body: request,
            });
        },

        onSuccess: () => {
            onClose();
            showToast({
                type: 'success',
                content: 'Successfully updated model assignment',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update model assignment',
            }),
    });

    const onSubmit = handleSubmit((data) => saveAssignment(data));

    return (
        <ActionModal
            title="Edit Model Assignment"
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={750}
            width={1300}
            contentNoPadding
            onClose={onClose}
            onAction={onSubmit}
            actionable={
                !isSubmitting && !isSaving && !isSaveError && !(isSubmitted && !isValid)
            }
            footerContent={
                isSaveError ? (
                    <FormError message="Unable to edit model assignment." />
                ) : undefined
            }
        >
            <Columns>
                <Column>
                    <Label label="Root Group">
                        <SpinnerArea isLoading={isLoadingRootGroup}>
                            <GroupHierarchyText
                                hierarchy={rootGroup?.Hierarchy}
                                suffix={rootGroup?.Name}
                                prefix={hierarchyPrefix}
                            />
                        </SpinnerArea>
                    </Label>
                    <ValidatableLabel
                        className="mt-20"
                        label="Assigned Groups"
                        required
                        error={errors.groupIds}
                    >
                        <Controller
                            name="groupIds"
                            control={control}
                            rules={groupRules}
                            render={({ field }) => (
                                <SpinnerArea isLoading={isLoadingInitialExpansions}>
                                    <ModelAggregateGroupsInput
                                        treeId="edit-analytics-model-assignment-tree"
                                        expandedGroupIds={expandedGroupIds}
                                        onExpandedChange={setExpandedGroupIds}
                                        selectedGroupIds={field.value}
                                        onSelectedChange={field.onChange}
                                        rootGroupId={assignment.RootGroup.RootGroupId}
                                    />
                                </SpinnerArea>
                            )}
                        />
                    </ValidatableLabel>
                </Column>
                <Column>
                    <SpinnerArea isLoading={modelLoading}>
                        {properties.length > 0 ? (
                            properties.map((property, index) => (
                                <ValidatableLabel
                                    key={property.PropertyId}
                                    className="mb-20"
                                    label={property.PropertyName}
                                    required={property.PropertyRequired}
                                    error={errors.parameters?.[index]?.PropertyValue}
                                >
                                    {renderParameterInput(form, property, index)}
                                    <input
                                        hidden
                                        {...register(
                                            `parameters.${index}.PropertyId` as const,
                                            {
                                                required: true,
                                            },
                                        )}
                                    />
                                    <FormError
                                        message={
                                            errors.parameters?.[index]?.PropertyValue
                                                ?.message
                                        }
                                    />
                                </ValidatableLabel>
                            ))
                        ) : (
                            <div>No input needed for this model</div>
                        )}
                    </SpinnerArea>
                </Column>
            </Columns>
        </ActionModal>
    );
}
